/*
* Created on Feb 19, 2021
* @author: Adam Cheng <adamcheng@qnap.com>
*/
import { useState, useRef, useEffect } from 'react';
import ic_tag_group_svg from "../../static/img/organization/ic-tag-group.svg";
import ic_tag_user_svg from "../../static/img/organization/ic-tag-user.svg";
import ic_tmp_char from "../../static/img/temp/chara_120x120.png";
import ic_avatar_group_svg from "../../static/img/utils/ic-avatar-group.svg";
import {useQuery} from "@tanstack/react-query";
import {useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useOrganizationApi} from "../../hook/OrganizationAPIHook";
import {useRegion} from "../../context/RegionContext";
import {deriveSiteAdminSelection, toggleAdminSelectionList} from "./siteAdminUtils";


const SiteAdminSelectorComponent = ({input, setInput, handleInputChange, setAdminChangeValues}) => {
    const {t} = useTranslation();
    const {OrganizationAPI} = useOrganizationApi();
    const {oid} = useParams();
    const [showAdminPicker, setShowAdminPicker] = useState(false);
    const node = useRef()
    const [users, setUsers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [count, setCount] = useState(5);
    const [query, setQuery] = useState('');
    const {url} = useRegion();

    const { } = useQuery({
        queryKey: [`organization/${oid}/group`],
        queryFn: async () => {
            const group_list = await OrganizationAPI.get(`organization/${oid}/group`, {limit: 200});
            const _gl = group_list.result.map(g => {
                if (input.admin.value.groups.find((_g) => _g.id === g.organization_group_id)) {
                    return {...g, checked: true}
                }
                return g
            });
            setGroups(_gl);
            setInput((prevInput) => {
                return {
                    ...prevInput,
                    admin: {
                        value: {
                            users: prevInput.admin.value.users,
                            groups: _gl.filter(g => g.checked)
                        }
                    }
                }
            });
            return _gl;
        }
    });

    const { } = useQuery({
        queryKey: [`organization/${oid}/user`],
        queryFn: async () => {
            const user_list = await OrganizationAPI.get(`organization/${oid}/user`, {limit: 200});
            const _ul = user_list.result.map(u => {
                if (input.admin.value.users.find((_u) => _u.id === u.id)) {
                    return {...u, checked: true}
                }
                return u
            });
            setUsers(_ul);
            setInput((prevInput) => {
                return {
                    ...prevInput,
                    admin: {
                        value: {
                            users: _ul.filter(u => u.checked),
                            groups: prevInput.admin.value.groups
                        }
                    }
                }
            });
            return _ul;
        }});

    const toggleAdmin = (type, admin) => {
        if (type === 'users') {
            setUsers((prevUsers) => {
                const nextUsers = toggleAdminSelectionList(prevUsers, 'id', admin.id);
                const selectedUsers = deriveSiteAdminSelection(nextUsers);

                setAdminChangeValues((prev) => ({
                    ...prev,
                    user: {
                        ...(prev?.user || {}),
                        [admin.id]: nextUsers.find((user) => user.id === admin.id),
                    },
                }));

                setInput((prevInput) => {
                    const selectedGroups = prevInput.admin.value.groups || [];
                    setCount(Math.max(0, 5 - (selectedUsers.length + selectedGroups.length)));
                    return {
                        ...prevInput,
                        admin: {
                            value: {
                                users: selectedUsers,
                                groups: selectedGroups,
                            },
                        },
                    };
                });

                return nextUsers;
            });
        }
        else if (type === 'groups') {
            setGroups((prevGroups) => {
                const nextGroups = toggleAdminSelectionList(prevGroups, 'organization_group_id', admin.organization_group_id);
                const selectedGroups = deriveSiteAdminSelection(nextGroups);

                setAdminChangeValues((prev) => ({
                    ...prev,
                    group: {
                        ...(prev?.group || {}),
                        [admin.organization_group_id]: nextGroups.find((group) => group.organization_group_id === admin.organization_group_id),
                    },
                }));

                setInput((prevInput) => {
                    const selectedUsers = prevInput.admin.value.users || [];
                    setCount(Math.max(0, 5 - (selectedUsers.length + selectedGroups.length)));
                    return {
                        ...prevInput,
                        admin: {
                            value: {
                                users: selectedUsers,
                                groups: selectedGroups,
                            },
                        },
                    };
                });

                return nextGroups;
            });
        }
    }

    const queryAdmin = (type, field) => {
        const regQuery = new RegExp(query, 'i');
        if (type === 'user') {
            return field.email?.match(regQuery) || field.display_name?.match(regQuery);
        }
        if (type === 'group') {
            return field.name.match(regQuery);
        }
    }

    const toggle = (e) => {
        // Check to see if the click is within the component itself.
        if (node.current && !node.current.contains(e.target)) {
            setShowAdminPicker(false);
        }
        else {
            setShowAdminPicker(true);
        }
    }

    useEffect(() => {
        // When mount a component
        document.addEventListener("click", toggle)

        // When unmount a component
        return () => {
            document.removeEventListener("click", toggle);
        }
    }, [])


    return (
        <div className="form-field" ref={node}>
            <div className="comp-input tag-input-inside" >
                {/*// <!-- tag input 輸入後樣式 -->*/}
                <div className={`comp-tag-input ${showAdminPicker ? 'hide' : ''}`} onClick={()=>setShowAdminPicker(true)}>
                    <div className="tag-input-preview">
                        {input.admin?.value.groups.length > 0 && (
                            input.admin.value.groups.slice(0, 4).map((g,index)=><span className="tag tag-tiny" key={`gt-${index}`}><span className="tag-label">{g.name}</span></span>)
                        )}
                        {input.admin?.value.users.length > 0 && count > 0 && (
                            input.admin.value.users.slice(0, count).map((u,index)=><span className="tag tag-tiny" key={`ut-${index}`}><span className="tag-label">{u.display_name}</span></span>)
                        )}
                        {input.admin?.value.groups.length + input.admin?.value.users.length > 5 &&
                            <span className="tag tag-tiny flex-none"><span className="tag-label">{input.admin.value.groups.length + input.admin.value.users.length - 5}+</span></span>
                        }

                    </div>
                </div>
                {/*// <!-- tag input 輸入中樣式 -->*/}
                <div className={`comp-tag-input ${showAdminPicker ? '' : 'hide'}`}>
                    {input.admin?.value.groups.length > 0 && (
                        input.admin.value.groups.map((g,index) => {
                            return (
                                <span className="tag tag-ic-front tag-user" key={`gtl-${index}`}>
                                    <span className="ic-img">
                                        <img src={ic_tag_group_svg} alt=""/>
                                    </span>
                                    <span className="tag-label">{g.name}</span>
                                    <span className="close" onClick={()=>toggleAdmin('groups', g)}/>
                                </span>
                            )
                        })
                    )}

                    {input.admin?.value.users.length > 0 && (
                        input.admin.value.users.map((u,index) => {
                            return (
                                <span className="tag tag-ic-front tag-user" key={`utl-${index}`}>
                                    <span className="ic-img">
                                        <img src={ic_tag_user_svg} alt=""/>
                                    </span>
                                    <span className="tag-label">{u.display_name}</span>
                                    <span className="close" onClick={()=>toggleAdmin('users', u)}/>
                                </span>
                            )
                        })
                    )}

                    <input type="text" placeholder={t("@Select administrators or groups@")} value={query} onChange={(e)=>setQuery(e.currentTarget.value)}/>
                </div>
                {/*// <!-- contacts hint -->*/}
                <div className={`comp-dropdown ${showAdminPicker ? '' : 'hide'}`}>
                    <ul className="comp-dropdown-menu">
                        <li className="section" style={{maxHeight: '22.75rem', overflowY: 'auto'}}>
                            <div className="section-head">{t('@Administrator@')}</div>
                            {users && (
                                users.filter(u=>u.status==='active').filter(u=>queryAdmin('user', u)).map((u, index) => {
                                    return (
                                        <div className="section-items" key={u.id}>
                                            <div className="contact-info">
                                                <div className="comp-checkbox no-label">
                                                    <input type="checkbox" id={`admin-${index}`}
                                                           checked={u.checked || false}
                                                           onChange={()=>toggleAdmin('users', u)}/>
                                                    <label htmlFor={`admin-${index}`} className="comp-label" />
                                                </div>
                                                <div className="account-img">
                                                    <div className={`avatar ${u.avatars?.icon? '' : 'no-avatar'}`}>
                                                        {u.avatars?.icon&& <img src={`${url.get_acc_url()}/avatar/${u.id}/s`} alt={''} />}
                                                        {!u.avatars && <span>
                                                            {u.display_name?.[0] || u.email?.[0] || u.qid?.[0]}
                                                        </span>}
                                                    </div>
                                                </div>
                                                <div className="account-info">
                                                    <div className="name">{u.display_name}</div>
                                                    <div className="mail">{u.email}</div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}

                            <div className="section-head">{t('@Group@')}</div>
                            {groups && (
                                groups.filter((g)=>queryAdmin('group', g)).map((g, index) => {
                                    return (
                                        <div className="section-items" key={g.organization_group_id}>
                                            <div className="contact-info">
                                                <div className="comp-checkbox no-label">
                                                    <input type="checkbox" id={`gcb-${index}`}
                                                           checked={g.checked || false}
                                                           onChange={()=>toggleAdmin('groups', g)} />
                                                    <label htmlFor={`gcb-${index}`} className="comp-label"/>
                                                </div>
                                                <div className="account-img">
                                                    <div className="avatar"><img src={ic_avatar_group_svg} alt=""/></div>
                                                </div>
                                                <div className="account-info">
                                                    <div className="name">{g.name}</div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default SiteAdminSelectorComponent;
